import { isBrowser } from "@common/lib/misc";
import { type UniqueIdentifier } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import type { FlattenedItem, TreeItem, TreeItems } from "./types";

export const iOS = isBrowser() ? /iPad|iPhone|iPod/.test(navigator.platform) : false;

function getDragDepth(offset: number, indentationWidth: number) {
  return Math.round(offset / indentationWidth);
}

function getMaxDepth<T>({ previousItem }: { previousItem: FlattenedItem<T> }) {
  if (previousItem) {
    return previousItem.depth + 1;
  }

  return 0;
}

function getMinDepth<T>({ nextItem }: { nextItem: FlattenedItem<T> }) {
  if (nextItem) {
    return nextItem.depth;
  }

  return 0;
}

type Projection = {
  depth: number;
  maxDepth: number;
  minDepth: number;
  parentId: UniqueIdentifier | null;
};
export function getProjection<T>(
  items: FlattenedItem<T>[],
  activeId: UniqueIdentifier,
  overId: UniqueIdentifier,
  dragOffset: number,
  indentationWidth: number,
  isSameDepthDrag: boolean
): Projection {
  const activeItemIndex = items.findIndex(({ id }) => id === activeId);
  const activeItem = items[activeItemIndex];

  const overItemIndex = items.findIndex(({ id }) => id === overId);

  const newItems = arrayMove(items, activeItemIndex, overItemIndex);
  const previousItem = newItems[overItemIndex - 1];
  const nextItem = newItems[overItemIndex + 1];
  const dragDepth = getDragDepth(dragOffset, indentationWidth);
  const projectedDepth = activeItem.depth + dragDepth;
  const maxDepth = isSameDepthDrag
    ? activeItem.depth
    : getMaxDepth({
        previousItem
      });
  const minDepth = isSameDepthDrag ? activeItem.depth : getMinDepth({ nextItem });

  let depth = projectedDepth;

  if (projectedDepth >= maxDepth) {
    depth = maxDepth;
  } else if (projectedDepth < minDepth) {
    depth = minDepth;
  }

  return {
    depth,
    maxDepth,
    minDepth,
    parentId: getParentId()
  };

  function getParentId() {
    if (depth === 0 || !previousItem) {
      return null;
    }

    if (depth === previousItem.depth) {
      return previousItem.parentId;
    }

    if (depth > previousItem.depth) {
      return previousItem.id;
    }

    const newParent = newItems
      .slice(0, overItemIndex)
      .reverse()
      .find((item) => item.depth === depth)?.parentId;

    return newParent ?? null;
  }
}

function flatten<T extends Record<string, any>>(
  items: TreeItems<T>,
  parentId: UniqueIdentifier | null = null
): FlattenedItem<T>[] {
  const _flatten = (items: TreeItems<T>, parentId: UniqueIdentifier | null, depth: number) => {
    return items.reduce<FlattenedItem<T>[]>((acc, item, index) => {
      const flattenedItem: FlattenedItem<T> = {
        ...item,
        parentId,
        depth,
        index,
        isLast: items.length === index + 1,
        isHighlighted: false
      };
      return [...acc, flattenedItem, ..._flatten(item.children ?? [], item.id, depth + 1)];
    }, []);
  };

  return _flatten(items, parentId, 0);
}

export function flattenTree<T extends Record<string, any>>(items: TreeItems<T>): FlattenedItem<T>[] {
  return flatten(items, null);
}

export function buildTree<T extends Record<string, any>>(flattenedItems: FlattenedItem<T>[]): TreeItems<T> {
  const root: TreeItem<T> = { id: "root", children: [] } as any;
  const nodes: Record<string, TreeItem<T>> = { [root.id]: root };
  const items = flattenedItems.map((item) => ({ ...item, children: [] }));

  for (const item of items) {
    const { id } = item;
    const parentId = item.parentId ?? root.id;
    const parent = nodes[parentId] ?? findItem(items, parentId);
    nodes[id] = item;
    parent?.children?.push(item);
  }

  return root.children ?? [];
}

export function findItem<T>(items: TreeItem<T>[], itemId: UniqueIdentifier) {
  return items.find(({ id }) => id === itemId);
}

export function findItemDeep<T extends Record<string, any>>(
  items: TreeItems<T>,
  itemId: UniqueIdentifier
):
  | {
      item: TreeItem<T>;
      index: number;
    }
  | undefined {
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const { id, children } = item;

    if (id === itemId) {
      return { item, index: i };
    }

    if (children?.length) {
      const child = findItemDeep(children, itemId);

      if (child) {
        return child;
      }
    }
  }
}

export function removeItem<T extends Record<string, any>>(items: TreeItems<T>, id: string) {
  const newItems: TreeItems<T> = [];

  for (const item of items) {
    if (item.id === id) {
      continue;
    }

    if (item.children?.length) {
      item.children = removeItem(item.children, id);
    }

    newItems.push(item);
  }

  return newItems;
}

export function removeChildrenOf<T>(items: FlattenedItem<T>[], ids: UniqueIdentifier[]) {
  const excludeParentIds = [...ids];

  return items.filter((item) => {
    if (item.parentId && excludeParentIds.includes(item.parentId)) {
      if (item.children?.length) {
        excludeParentIds.push(item.id);
      }
      return false;
    }

    return true;
  });
}
